import { dayjs } from "@marekl/adminer/datetime";
import type { Membership, Person } from "../../../lib/queries/memberships";
import type { ID } from "../../../lib/id";
import { emptyPerson, type DraftPerson, type DraftRow } from "./draftTypes";

/**
 * Row builders shared by the reducer and the merge (draftRebase), kept apart
 * so neither has to import the other.
 */

/** A row nobody has merged: no conflicts, nothing orphaned or duplicated. */
export const NO_MERGE_STATE = { conflicts: {}, orphaned: false, duplicateOf: null } as const satisfies Pick<
  DraftRow,
  "conflicts" | "orphaned" | "duplicateOf"
>;

export function personToDraft(p: Person): DraftPerson {
  return {
    first_name: p.first_name,
    last_name: p.last_name,
    country_code: p.country_code || "CZ",
    birth_date: p.birth_date ? dayjs(p.birth_date) : null,
    birth_number: p.birth_number ?? "",
    street: p.street ?? "",
    city: p.city ?? "",
    postal_code: p.postal_code ?? "",
    phone: p.phone ?? "",
  };
}

/**
 * rosterSignature detects that someone else changed the roster underneath
 * us. It must cover every field the draft hydrates — including the person
 * values — or a concurrent person edit raises no stale banner and the next
 * submit silently reverts it (the batch sends the full hydration-time
 * snapshot for any dirty row).
 */
export function rosterSignature(members: Membership[]): string {
  return members
    .map((m) => {
      const p = m.person;
      const person = [
        p.first_name, p.last_name, p.country_code, p.birth_date ?? "",
        p.birth_number ?? "", p.street ?? "", p.city ?? "",
        p.postal_code ?? "", p.phone ?? "",
      ].join("\u0000");
      return `${m.id}:${p.id}:${m.kind}:${[...m.roles].sort().join("|")}:${person}`;
    })
    .sort()
    .join(",");
}

export function persistedRow(m: Membership, leaderPersonId: ID | null): DraftRow {
  return {
    key: `m:${m.id}`,
    origin: { kind: "persisted", membershipId: m.id, personId: m.person.id },
    baseline: m.person,
    baselineRoles: [...m.roles],
    person: personToDraft(m.person),
    roles: [...m.roles],
    removed: false,
    isLeader: m.person.id === leaderPersonId,
    serverErrors: {},
    ...NO_MERGE_STATE,
  };
}

export function transferRow(m: Membership, leaderPersonId: ID | null): DraftRow {
  return {
    key: `p:${m.person.id}`,
    origin: { kind: "transfer", personId: m.person.id, sourceSeasonId: m.season_id },
    baseline: m.person,
    baselineRoles: [],
    person: personToDraft(m.person),
    roles: m.roles.length > 0 ? [...m.roles] : ["athlete"],
    removed: false,
    isLeader: m.person.id === leaderPersonId,
    serverErrors: {},
    ...NO_MERGE_STATE,
  };
}

export function blankRow(id: number): DraftRow {
  return {
    key: `new:${id}`,
    origin: { kind: "new" },
    baseline: null,
    baselineRoles: [],
    person: { ...emptyPerson },
    roles: ["athlete"],
    removed: false,
    isLeader: false,
    serverErrors: {},
    ...NO_MERGE_STATE,
  };
}
